import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/inventory/store-items/`;
const CAT_URL = `/api/inventory/item-categories/`;

// ── Store items ──────────────────────────────────────────────────────────────
export const fetchItems = createAsyncThunk('store/fetchItems', async () => {
  const res = await axios.get(BASE_URL);
  return res.data;
});

export const addItem = createAsyncThunk('store/addItem', async (item) => {
  const res = await axios.post(BASE_URL, item);
  return res.data;
});

export const updateItem = createAsyncThunk('store/updateItem', async ({ id, count }) => {
  const res = await axios.patch(`${BASE_URL}${id}/`, { count });
  return res.data;
});

export const deleteItem = createAsyncThunk('store/deleteItem', async (id) => {
  await axios.delete(`${BASE_URL}${id}/`);
  return id;
});

// ── Item categories ──────────────────────────────────────────────────────────
export const fetchItemCategories = createAsyncThunk(
  'store/fetchItemCategories',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(CAT_URL);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const createItemCategory = createAsyncThunk(
  'store/createItemCategory',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(CAT_URL, data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const updateItemCategory = createAsyncThunk(
  'store/updateItemCategory',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${CAT_URL}${id}/`, data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const deleteItemCategory = createAsyncThunk(
  'store/deleteItemCategory',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${CAT_URL}${id}/`);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

const storeSlice = createSlice({
  name: 'store',
  initialState: {
    items: [],
    itemCategories: [],
    loading: false,
    categoriesLoading: false,
    categoriesSaving: false,
    error: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      // Store items
      .addCase(fetchItems.pending, (state) => { state.loading = true; })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchItems.rejected, (state) => { state.loading = false; })
      .addCase(addItem.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        const i = state.items.findIndex(x => x.id === action.payload.id);
        if (i !== -1) state.items[i] = action.payload;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.items = state.items.filter(x => x.id !== action.payload);
      })
      // Item categories
      .addCase(fetchItemCategories.pending, (state) => { state.categoriesLoading = true; })
      .addCase(fetchItemCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.itemCategories = action.payload;
      })
      .addCase(fetchItemCategories.rejected, (state) => { state.categoriesLoading = false; })
      .addCase(createItemCategory.pending, (state) => { state.categoriesSaving = true; })
      .addCase(createItemCategory.fulfilled, (state, action) => {
        state.categoriesSaving = false;
        state.itemCategories.push(action.payload);
        state.itemCategories.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(createItemCategory.rejected, (state) => { state.categoriesSaving = false; })
      .addCase(updateItemCategory.pending, (state) => { state.categoriesSaving = true; })
      .addCase(updateItemCategory.fulfilled, (state, action) => {
        state.categoriesSaving = false;
        const i = state.itemCategories.findIndex(x => x.id === action.payload.id);
        if (i !== -1) state.itemCategories[i] = action.payload;
      })
      .addCase(updateItemCategory.rejected, (state) => { state.categoriesSaving = false; })
      .addCase(deleteItemCategory.fulfilled, (state, action) => {
        state.itemCategories = state.itemCategories.filter(x => x.id !== action.payload);
      });
  }
});

export default storeSlice.reducer;
